
























































































import { defineComponent } from '@vue/composition-api';
import Flex from '@/features/ui/layout/Flex.global.vue';
import Cell from '@/features/ui/layout/Cell.global.vue';
import Datepicker from '@/components/datepicker/Datepicker.global.vue';
import Table from '@/features/ui/table/Table.global.vue';
import TableSkeleton from '@/features/ui/table/TableSkeleton.global.vue';
import { EiqMessage } from './StatusTab.vue';
import { EnerIQSuggestionList } from '@/util/heating-systems';
import { getEnerIQSeverityColor } from '@/util/modbus';
import Pagination from '@/features/ui/pagination/Pagination.global.vue';
import {
  getEndOfDayTimestamp,
  getStartOfDayTimestamp,
  isDateRangeInvalid,
} from '@/features/app-enterprise-project/views/enterprise-project-audit-logs/helpers/date-time-utils';
import { mapActions } from 'vuex';
import WizardButton from '@/components/clickables/WizardButton.global.vue';

export default defineComponent({
  components: {
    Flex,
    Cell,
    Datepicker,
    Table,
    TableSkeleton,
    Pagination,
    WizardButton,
  },
  props: {
    componentList: {
      type: Array as () => { spotId: string; groupName: string }[],
      required: true,
    },
  },
  data() {
    const today = new Date().toISOString().slice(0, 10);

    return {
      searchText: '',
      fromDate: today,
      toDate: today,
      loading: false,
      eneriqResults: [] as EnerIQSuggestionList[],
      lastResult: null,
      selectedSpotId: '',
      currentPage: 1,
      pageSize: 5,
      columns: [
        { name: 'score', label: 'Score' },
        { name: 'analyse', label: 'Analyse' },
        { name: 'handlung', label: 'Handlungsempfehlung' },
        { name: 'datum', label: 'Zeitstempel' },
      ],
      skipNextFromDateChange: false,
      skipNextToDateChange: false,
    };
  },
  computed: {
    filteredRows(): Array<{ score: number; analyse: string; handlung: string; datum: string; severity: string }> {
      return this.processEnerIQResults().flatMap((item) => {
        // For each message, split the pairs
        return item.messages.flatMap((m) =>
          this.splitMessage(m.message).map((pair) => ({
            score: item.score,
            analyse: pair.analyse,
            handlung: pair.handlung,
            datum: item.lastPayload,
            severity: m.color || 'gray',
          })),
        );
      });
    },
    paginatedRows(): Array<{ score: number; analyse: string; handlung: string; datum: string }> {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredRows.slice(start, start + this.pageSize);
    },
    totalPages(): number {
      return Math.ceil(this.filteredRows.length / this.pageSize);
    },
    disabledFutureDates(): { from: Date } {
      const now = new Date();
      const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
      return {
        from: tomorrow,
      };
    },
  },
  watch: {
    currentPage() {
      this.$nextTick(() => {
        this.scrollTableToTop();
      });
    },
  },
  mounted() {
    if (this.componentList.length > 0) {
      this.selectedSpotId = this.componentList[0].spotId;
      this.fetchEnerIQSuggestions(this.componentList[0].spotId, this.fromDate, this.toDate).then((result) => {
        if (result) {
          this.eneriqResults = result;
        }
      });
    }
  },
  methods: {
    ...mapActions({
      addToastMessages: 'ADD_TOAST_MESSAGES',
    }),
    async fetchEnerIQSuggestions(
      spotId: string,
      fromDate?: string | null | undefined,
      toDate?: string | null | undefined,
      page = 1,
      limit = 100000, // a bit far-fetched but safe to get all entries
    ): Promise<EnerIQSuggestionList[] | null> {
      this.loading = true;
      const params = new URLSearchParams();
      params.append('spotId', spotId);
      params.append('page', String(page - 1));
      params.append('limit', String(limit));
      if (fromDate) {
        const start = getStartOfDayTimestamp(new Date(fromDate));
        if (start) params.append('fromDate', start.toISOString());
      }
      if (toDate) {
        const end = getEndOfDayTimestamp(new Date(toDate));
        if (end) params.append('toDate', end.toISOString());
      }

      try {
        const res = await this.$store.dispatch('getEnerIQSuggestions', params.toString());
        if (!res.ok) {
          this.loading = false;
          return null;
        }
        const result = await res.json();

        this.loading = false;
        return result;
      } catch (error) {
        this.loading = false;
        return null;
      }
    },
    onComponentSelect(spotId: string) {
      if (this.selectedSpotId === spotId) {
        return;
      }
      this.selectedSpotId = spotId;
      this.currentPage = 1;
      this.fetchEnerIQSuggestions(spotId, this.fromDate, this.toDate).then((result) => {
        this.eneriqResults = result || [];
        this.$nextTick(() => {
          this.scrollTableToTop();
        });
      });
    },
    processEnerIQResults(): Array<{
      spotId: string;
      score: number;
      lastPayload: string;
      messages: EiqMessage[];
    }> {
      return (this.eneriqResults as EnerIQSuggestionList[]).map((suggestionList: EnerIQSuggestionList) => {
        const { operations } = suggestionList.suggestion.score.metadata.reason;
        const messages: EiqMessage[] = Object.values(operations)
          .filter((operation) => operation.severity !== -1)
          .map((operation) => {
            const message =
              typeof operation.description === 'object' ? operation.description.de : operation.description;
            return {
              color: getEnerIQSeverityColor(operation.severity),
              message,
            };
          });
        return {
          spotId: suggestionList.spotId,
          score: suggestionList.suggestion.score.score,
          lastPayload: suggestionList.suggestion.score.time ?? 'N/A',
          messages,
        };
      });
    },
    async validateAndResetFromDate(update: { oldValue: string }): Promise<void> {
      if (isDateRangeInvalid(new Date(this.fromDate), new Date(this.toDate))) {
        await this.addToastMessages({
          messages: [{ text: 'Das Datum von kann nicht nach dem Datum bis liegen.', class: 'error' }],
        });
        this.skipNextFromDateChange = true;
        this.fromDate = update.oldValue;
      }
    },
    async onFromDateChange(update: { oldValue: string }): Promise<void> {
      if (this.skipNextFromDateChange) {
        this.skipNextFromDateChange = false;
        return; // Don't call API after programmatic reset
      }
      await this.validateAndResetFromDate(update);
      if (!isDateRangeInvalid(new Date(this.fromDate), new Date(this.toDate))) {
        this.currentPage = 1;
        const result = await this.fetchEnerIQSuggestions(this.selectedSpotId, this.fromDate, this.toDate);
        this.eneriqResults = result || [];
        this.$nextTick(() => {
          this.scrollTableToTop();
        });
      }
    },
    async validateAndResetUntilDate(update: { oldValue: string }): Promise<void> {
      if (isDateRangeInvalid(new Date(this.fromDate), new Date(this.toDate))) {
        await this.addToastMessages({
          messages: [{ text: 'Das Datum von kann nicht nach dem Datum bis liegen.', class: 'error' }],
        });
        this.skipNextToDateChange = true;
        this.toDate = update.oldValue;
      }
    },
    async onUntilDateChange(update: { oldValue: string }): Promise<void> {
      if (this.skipNextToDateChange) {
        this.skipNextToDateChange = false;
        return; // Don't call API after programmatic reset
      }
      await this.validateAndResetUntilDate(update);
      if (!isDateRangeInvalid(new Date(this.fromDate), new Date(this.toDate))) {
        this.currentPage = 1;
        const result = await this.fetchEnerIQSuggestions(this.selectedSpotId, this.fromDate, this.toDate);
        this.eneriqResults = result || [];
        this.$nextTick(() => {
          this.scrollTableToTop();
        });
      }
    },
    splitMessage(message: string): { analyse: string; handlung: string }[] {
      const result: { analyse: string; handlung: string }[] = [];
      const regex =
        /(.*?)\*{0,3}Handlungsempfehlungen:?\*{0,3}\s*([\s\S]*?)(?=(?:\*{0,3}Handlungsempfehlungen:?\*{0,3}|$))/g;
      let match;
      while ((match = regex.exec(message)) !== null) {
        let analyse = match[1].trim().replace(/\*+/g, '');
        const handlung = match[2].trim().replace(/\*+/g, '');
        // If analysis is empty, fallback to everything before Handlungsempfehlungen
        if (!analyse && message.includes('Handlungsempfehlungen:')) {
          analyse = message.split('Handlungsempfehlungen:')[0].trim().replace(/\*+/g, '');
        }
        result.push({ analyse, handlung });
      }
      if (result.length === 0) {
        result.push({ analyse: message, handlung: '' });
      }
      return result;
    },
    formattedMessage(message: string): string {
      let formatted = message.replace(/Handlungsempfehlungen:/g, '<strong>Handlungsempfehlungen:</strong>');
      formatted = formatted.replace(/\n+/g, '<br>');
      // This line fixes the first bullet issue:
      formatted = formatted.replace(/(^|<br>)- (.*?)(?=(<br>|$))/g, '$1<li>$2</li>');
      formatted = formatted.replace(
        /((?:<li>.*?<\/li>)+)/g,
        '<ul style="margin: 0.5em 0 0.5em 1.2em; padding: 0;">$1</ul>',
      );
      formatted = formatted.replace(/<br><ul/g, '<ul');
      formatted = formatted.replace(/\*+/g, '');

      return this.sanitizeHtml(formatted);
    },
    sanitizeHtml(html: string): string {
      // Remove script/style tags and their content
      html = html.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '');
      html = html.replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '');
      // Remove event handlers like onclick, onerror, etc.
      html = html.replace(/ on\w+="[^"]*"/gi, '');
      html = html.replace(/ on\w+='[^']*'/gi, '');
      // Remove javascript: in href/src
      html = html.replace(/(href|src)\s*=\s*(['"])javascript:[^'"]*\2/gi, '$1="#"');
      return html;
    },
    formatHandlungAsList(handlung: string): string[] {
      return handlung
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.startsWith('-'))
        .map((line) => line.replace(/^-/, '').trim());
    },
    onPageChange(page: number) {
      this.currentPage = page;
    },
    scrollTableToTop() {
      this.$nextTick(() => {
        const el = this.$el.querySelector('.table-content');
        if (el) el.scrollTop = 0;
      });
    },
    showNoDataMessage() {
      return !this.loading && this.paginatedRows.length === 0;
    },
  },
});
